import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { IMAGES } from "@/data/content";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { SignOut, CaretDown, Trash, EnvelopeSimple, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_STYLE = {
  new: "bg-[#C8A96A]/15 text-[#C8A96A] border-[#C8A96A]/40",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  closed: "bg-[#6B7280]/20 text-[#9ca3af] border-[#6B7280]/40",
};

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-[#F6F4F0]/10 bg-[#101f30] p-8">
    <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">{label}</p>
    <p className="font-heading text-5xl text-[#F6F4F0]">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/consultations"), api.get("/stats")]);
      setRows(c.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/consultations/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/consultations/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-navy">
      <header className="border-b border-[#F6F4F0]/10 backdrop-blur-xl bg-[#0D1B2A]/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="4PS" className="h-10 w-auto object-contain" />
            <div>
              <p className="font-heading text-lg text-[#F6F4F0] leading-none">Consultation Desk</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-1">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="refresh-btn" onClick={load} className="text-[#6B7280] hover:text-gold transition-colors" aria-label="Refresh">
              <ArrowClockwise size={20} />
            </button>
            <button data-testid="logout-btn" onClick={handleLogout} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#6B7280] text-[#E5E7EB] hover:border-[#C8A96A] hover:text-gold transition-colors">
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          <StatCard label="Total" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} testid="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" />
          <StatCard label="Closed" value={stats.closed} testid="stat-closed" />
        </div>

        <h2 className="font-heading text-3xl text-[#F6F4F0] mb-8">Submissions</h2>

        <div className="border border-[#F6F4F0]/10 bg-[#101f30] overflow-x-auto">
          {loading ? (
            <div className="p-16 text-center text-gold text-xs uppercase tracking-[0.3em] animate-pulse">Loading…</div>
          ) : rows.length === 0 ? (
            <div data-testid="empty-state" className="p-16 text-center text-[#6B7280]">No consultation requests yet.</div>
          ) : (
            <Table data-testid="consultations-table">
              <TableHeader>
                <TableRow className="border-[#F6F4F0]/10 hover:bg-transparent">
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">Name</TableHead>
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">Business</TableHead>
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">Interest</TableHead>
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">Preferred</TableHead>
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">Status</TableHead>
                  <TableHead className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={`consultation-row-${r.id}`}
                    onClick={() => setSelected(r)}
                    className="border-[#F6F4F0]/10 cursor-pointer hover:bg-[#0D1B2A]/60"
                  >
                    <TableCell className="text-[#F6F4F0]">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-[#6B7280]">{r.email}</div>
                    </TableCell>
                    <TableCell className="text-[#E5E7EB]">{r.business}</TableCell>
                    <TableCell className="text-[#E5E7EB]">{r.interest || "—"}</TableCell>
                    <TableCell className="text-[#E5E7EB]">{r.preferred_date || "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-3 py-1 border rounded-full ${STATUS_STYLE[r.status] || STATUS_STYLE.new}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`status-menu-${r.id}`} className="inline-flex items-center gap-1 text-xs text-gold hover:text-[#F6F4F0]">
                            Set Status <CaretDown size={12} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#101f30] border-[#F6F4F0]/10 text-[#E5E7EB]">
                          {["new", "contacted", "closed"].map((s) => (
                            <DropdownMenuItem key={s} data-testid={`status-${s}-${r.id}`} onClick={() => setStatus(r.id, s)} className="capitalize cursor-pointer focus:bg-[#0D1B2A] focus:text-gold">
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div data-testid="detail-modal" className="w-full max-w-lg bg-[#101f30] border border-[#C8A96A]/30 p-10 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-heading text-3xl text-[#F6F4F0]">{selected.name}</h3>
                <p className="text-[#6B7280] text-sm mt-1">{selected.business}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-3 py-1 border rounded-full ${STATUS_STYLE[selected.status]}`}>{selected.status}</span>
            </div>
            <dl className="space-y-4 text-sm">
              {[
                ["Email", selected.email],
                ["Phone", selected.phone || "—"],
                ["Interested In", selected.interest || "—"],
                ["Budget", selected.budget || "—"],
                ["Preferred Date", selected.preferred_date || "—"],
                ["Submitted", new Date(selected.created_at).toLocaleString()],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-[#F6F4F0]/10 pb-3">
                  <dt className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em]">{k}</dt>
                  <dd className="text-[#E5E7EB] text-right break-all">{v}</dd>
                </div>
              ))}
              <div>
                <dt className="text-[#6B7280] uppercase text-[10px] tracking-[0.2em] mb-2">Message</dt>
                <dd className="text-[#E5E7EB] font-light leading-relaxed">{selected.message}</dd>
              </div>
            </dl>
            <div className="flex gap-3 mt-8">
              <a data-testid="modal-email-btn" href={`mailto:${selected.email}`} className="flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-4 bg-[#C8A96A] text-[#0D1B2A] hover:bg-[#F6F4F0] transition-colors font-medium">
                <EnvelopeSimple size={16} /> Email
              </a>
              <button data-testid="modal-delete-btn" onClick={() => remove(selected.id)} className="inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] px-6 py-4 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">
                <Trash size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
